import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Container,
  CircularProgress,
  Typography,
  Button,
  Grid,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/pt';
import 'dayjs/locale/en-gb';
import { useAppBar } from '../providers/AppBarProvider';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MainAgendaList from '../layouts/MainAgendaList';
import DayView from '../components/DayView';
import WeekView from '../components/WeekView';
import { useSnackbar } from "../providers/SnackBarProvider";
import {
  handleError,
  resolveDateFormattingLocale,
  bcp47ToDayjsLocale,
  getDayjsDateFormatForLocale,
  getDayjsMonthDayFormatForLocale,
  getDayjsDateTimeFormatForLocale,
  resolveHour12Preference,
  formatAgendaDateTitle,
} from '../utils/lib';
import useAgendaActions from '../hooks/useAgendaActions';
import { useConfig } from '../providers/ConfigProvider';
import { useTranslation } from 'react-i18next';

function MainAgenda() {
  const [agendaItems, setAgendaItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDateLoading, setIsDateLoading] = useState(false); // Loading sutil para cambios de fecha
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [viewMode, setViewMode] = useState('lista'); // 'dia', 'semana', 'lista'
  const navigate = useNavigate();
  const { updateTitle, toggleFab } = useAppBar();
  const snackbar = useSnackbar();
  const { config } = useConfig();
  const { t } = useTranslation();

  const dateFormattingLocale = useMemo(() => resolveDateFormattingLocale(config), [config]);
  const dayjsPickerLocale = useMemo(
    () => bcp47ToDayjsLocale(dateFormattingLocale),
    [dateFormattingLocale]
  );
  const dateDisplayFormat = useMemo(
    () => getDayjsDateFormatForLocale(dateFormattingLocale),
    [dateFormattingLocale]
  );
  const monthDayDisplayFormat = useMemo(
    () => getDayjsMonthDayFormatForLocale(dateFormattingLocale),
    [dateFormattingLocale]
  );
  const useHour12 = useMemo(
    () => resolveHour12Preference(config, dateFormattingLocale),
    [config, dateFormattingLocale]
  );
  const dateTimeDisplayFormat = useMemo(
    () => getDayjsDateTimeFormatForLocale(dateFormattingLocale, useHour12),
    [dateFormattingLocale, useHour12]
  );

  // Usar el hook de acciones de agenda
  const {
    postponeDialog,
    completeDialog,
    handleReschedule,
    handlePostponeClose,
    handlePostponeConfirm,
    handleComplete,
    handleCompleteClose,
    handleCompleteConfirm,
    handleDateChange,
    AgendaDialogs
  } = useAgendaActions(
    // Callback onSuccess optimizado - recarga solo los datos necesarios
    async () => {
      // Recargar suavemente los datos de la fecha actual
      await fetchAgendaItems(1, true); // Reemplazar datos sin loading pesado
    },
    snackbar
  );

  useEffect(() => {
    toggleFab(true);
    
    if (config.rol === 'USER') {
      updateTitle('', t('main.personalAgenda'));
    } else {
      updateTitle('', t('main.agenda'));
    }

  }, []);

  // Optimized effect for date changes - no full reload
  useEffect(() => {
    const loadDataForDate = async () => {
      // Mostrar loading sutil para cambios de fecha
      setIsDateLoading(true);
      
      // Reset pagination for new date
      setCurrentPage(1);
      setHasMorePages(false);
      
      try {
        await fetchAgendaItems(1, true); // true = replace existing data
      } finally {
        setIsDateLoading(false);
        // Solo desactivar loading principal si estaba activo
        if (isLoading) {
          setIsLoading(false);
        }
      }
    };

    loadDataForDate();
  }, [selectedDate]);

  // Recargar datos cuando cambie el modo de vista
  useEffect(() => {
    setCurrentPage(1);
    setAgendaItems([]);
    fetchAgendaItems(1);
  }, [viewMode]);

  const fetchAgendaItems = async (page, replaceData = false) => {
    // Solo mostrar loading principal si es página 1 y no estamos reemplazando datos suavemente
    if (page === 1 && !replaceData) {
      setIsLoading(true);
    } else if (page > 1) {
      setIsLoadingMore(true);
    }
    
    try {
      let params = {};
      
      if (viewMode === 'dia') {
        // Para vista de día, usar date_min y date_max sin paginación
        const startOfDay = selectedDate.startOf('day').format('YYYY-MM-DD');
        const endOfDay = selectedDate.endOf('day').format('YYYY-MM-DD');
        params = {
          date_min: startOfDay,
          date_max: endOfDay
        };
      } else if (viewMode === 'semana') {
        // Para vista de semana, usar date_min y date_max para toda la semana
        const startOfWeek = selectedDate.startOf('week').add(1, 'day').format('YYYY-MM-DD'); // Lunes
        const endOfWeek = selectedDate.startOf('week').add(7, 'day').format('YYYY-MM-DD'); // Domingo
        params = {
          date_min: startOfWeek,
          date_max: endOfWeek
        };
      } else {
        // Para vista lista, usar el formato original con paginación
        const formattedDate = selectedDate.format('YYYY-MM-DD');
        params = {
          selected_date: formattedDate,
          page: page
        };
      }

      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}agenda`,
        { 
          params: params,
          withCredentials: true 
        }
      );
      
      //console.log(response.data);

      let content, total_pages;
      
      if (viewMode === 'dia' || viewMode === 'semana') {
        // Para vista de día y semana, los datos vienen directamente en data
        content = response.data.data || [];
        total_pages = 1; // No hay paginación en vista de día/semana
      } else {
        // Para vista lista, los datos vienen en data.data.content
        const responseData = response.data.data || {};
        content = responseData.content || [];
        total_pages = responseData.total_pages || 1;
      }

      const has_more = viewMode !== 'dia' && viewMode !== 'semana' && page < total_pages;

      // Validar que content existe
      const validContent = content;
      
      if (page === 1 || replaceData) {
        setAgendaItems(validContent);
      } else {
        setAgendaItems(prev => [...(prev || []), ...validContent]);
      }
      
      setHasMorePages(has_more);
      setCurrentPage(page);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (hasMorePages && !isLoadingMore) {
      fetchAgendaItems(currentPage + 1);
    }
  };

  const handleItemClick = (pet) => {
    navigate('/main/view-pet', { state: { id: pet.id, updateLastView: false } });
  };

  const handleAgendaEventClick = (event, item) => {
    event.stopPropagation();
    navigate('/main/view-agenda-event', { state: { id: item.id } });
  };

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  if (isLoading) return (
    <>
      <CircularProgress
        size={42}
        sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-21px', marginLeft: '-21px' }}
      />
    </>
  );
  
  return (
    <Container maxWidth="xl">
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsPickerLocale}>
        <Grid container spacing={3}>
          {/* Calendario */}
          <Grid item xs={12}>
            <Paper 
              elevation={3}
              sx={{ 
                p: 2,
                borderRadius: 2,
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
                {t('agenda.calendar')}
              </Typography>
              <StaticDatePicker
                value={selectedDate}
                onChange={(newValue) => setSelectedDate(newValue)}
                displayStaticWrapperAs="desktop"
                views={['year', 'month', 'day']}
                format={dateDisplayFormat}
                slotProps={{
                  actionBar: { actions: [] }, // Oculta los botones de acción
                }}
              />
              
              {/* Botones de vista */}
              <Box sx={{ mt: 2 }}>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={handleViewModeChange}
                  aria-label={t('agenda.viewMode')}
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      fontSize: '0.75rem',
                      py: 0.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="dia" aria-label={t('agenda.dayView')}>
                    {t('agenda.day')}
                  </ToggleButton>
                  <ToggleButton value="semana" aria-label={t('agenda.weekView')}>
                    {t('agenda.week')}
                  </ToggleButton>
                  <ToggleButton value="lista" aria-label={t('agenda.listView')}>
                    {t('agenda.list')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Paper>
          </Grid>

          {/* Detalle debajo del calendario */}
          <Grid item xs={12}>
            <Paper 
              elevation={3}
              sx={{ 
                p: 2,
                borderRadius: 2,
                bgcolor: 'background.paper',
                minHeight: '400px'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  {viewMode === 'lista' && t('agenda.tasksFor', { date: formatAgendaDateTitle(selectedDate, dateFormattingLocale) })}
                  {viewMode === 'dia' && t('agenda.dayViewTitle', { date: formatAgendaDateTitle(selectedDate, dateFormattingLocale) })}
                  {viewMode === 'semana' && t('agenda.weekViewTitle', {
                    start: formatAgendaDateTitle(selectedDate.startOf('week').add(1, 'day'), dateFormattingLocale),
                    end: formatAgendaDateTitle(selectedDate.startOf('week').add(7, 'day'), dateFormattingLocale),
                  })}
                </Typography>
                {isDateLoading && (
                  <CircularProgress size={20} sx={{ ml: 2 }} />
                )}
              </Box>
              
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={42} />
                </Box>
              ) : viewMode === 'lista' ? (
                // Vista Lista (funcionalidad actual)
                agendaItems.length > 0 ? (
                  <>
                    <MainAgendaList
                      agendaItems={agendaItems}
                      onClick={handleItemClick}
                      onEventClick={handleAgendaEventClick}
                      onPostpone={handleReschedule}
                      onComplete={handleComplete}
                      dateTimeFormat={dateTimeDisplayFormat}
                    />
                    
                    {hasMorePages && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button 
                          variant="outlined" 
                          onClick={handleLoadMore}
                          disabled={isLoadingMore}
                        >
                          {isLoadingMore ? (
                            <CircularProgress size={24} />
                          ) : (
                            t('common.loadMore')
                          )}
                        </Button>
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography
                    variant="subtitle1"
                    sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}
                  >
                    {t('agenda.noTasks')}
                  </Typography>
                )
              ) : viewMode === 'dia' ? (
                // Vista de Día
                <DayView
                  agendaItems={agendaItems || []}
                  selectedDate={selectedDate}
                  onItemClick={handleItemClick}
                  onEventClick={handleAgendaEventClick}
                />
              ) : viewMode === 'semana' ? (
                // Vista de Semana
                <WeekView
                  agendaItems={agendaItems || []}
                  selectedDate={selectedDate}
                  onItemClick={handleItemClick}
                  onEventClick={handleAgendaEventClick}
                  monthDayFormat={monthDayDisplayFormat}
                />
              ) : (
                // Vista temporal para otras vistas futuras
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                    {t('agenda.viewNotAvailable')}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {t('agenda.viewComingSoon')}
                  </Typography>
                  <Button 
                    variant="outlined" 
                    onClick={() => setViewMode('lista')} 
                    sx={{ mt: 2 }}
                  >
                    {t('agenda.backToList')}
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </LocalizationProvider>

      {/* Diálogos de agenda usando el componente reutilizable */}
      <AgendaDialogs />

    </Container>
  );
}

export default MainAgenda;
